import { ChevronLeft, ChevronRight } from "./Icons";

interface Props {
  page: number;
  totalPages: number;
  total: number;
  pageSize: number;
  onPage: (p: number) => void;
  /** Omit to hide the rows-per-page selector (fixed-size lists). */
  onPageSize?: (n: number) => void;
  /** "entries" by default; "documents", "events" etc. where clearer. */
  noun?: string;
}

/** Page numbers around the current one, with ellipses, never more than 7 buttons. */
function pageWindow(page: number, totalPages: number): (number | "gap")[] {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);

  const out: (number | "gap")[] = [1];
  const from = Math.max(2, page - 1);
  const to = Math.min(totalPages - 1, page + 1);

  if (from > 2) out.push("gap");
  for (let p = from; p <= to; p++) out.push(p);
  if (to < totalPages - 1) out.push("gap");
  out.push(totalPages);
  return out;
}

export function Pagination({
  page,
  totalPages,
  total,
  pageSize,
  onPage,
  onPageSize,
  noun = "entries",
}: Props) {
  const first = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);

  return (
    <div className="pager">
      <span className="pager-info">
        {total === 0
          ? `No ${noun}`
          : `Showing ${first} to ${last} of ${total} ${noun}`}
      </span>

      {onPageSize ? (
        <span className="pager-size">
          <label className="muted" htmlFor="page-size">Rows</label>
          <select
            id="page-size"
            value={pageSize}
            onChange={(e) => onPageSize(Number(e.target.value))}
          >
            {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </span>
      ) : null}

      <nav className="pager-pages" aria-label="Pagination">
        <button
          className="btn-icon"
          onClick={() => onPage(page - 1)}
          disabled={page <= 1}
          aria-label="Previous page"
        >
          <ChevronLeft />
        </button>

        {pageWindow(page, totalPages).map((p, i) =>
          p === "gap" ? (
            <span key={`gap-${i}`} className="pager-gap">…</span>
          ) : (
            <button
              key={p}
              onClick={() => onPage(p)}
              aria-current={p === page ? "page" : undefined}
              className={p === page ? "btn-primary" : ""}
              aria-label={`Page ${p}`}
            >
              {p}
            </button>
          )
        )}

        <button
          className="btn-icon"
          onClick={() => onPage(page + 1)}
          disabled={page >= totalPages}
          aria-label="Next page"
        >
          <ChevronRight />
        </button>
      </nav>
    </div>
  );
}
